import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Stack,
  Alert,
  CircularProgress,
  Chip,
  MenuItem,
  TableContainer,
  TextField,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import ReplayIcon from "@mui/icons-material/Replay";
import BusinessSettingsContext from "../Context/Settings/BusinessSettingsContext";
import {
  listCampaigns,
  retryQueuedCampaignMessages,
  CampaignDoc,
} from "../../services/campaign.service";
import {
  CampaignDateField,
  filterCampaignList,
} from "./campaignListFilters";

type Props = { businessId: string; onSelectCampaign: (id: string) => void };

export default function CampaignList({ businessId, onSelectCampaign }: Props) {
  const { settings } = useContext(BusinessSettingsContext);

  // uuid == businessId in your campaign feature
  const uuid =
    businessId ||
    (settings as any)?.businessSettings?.businessId ||
    (settings as any)?.businessId;

  const [items, setItems] = useState<CampaignDoc[]>([]);
  const [err, setErr] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [retryingId, setRetryingId] = useState("");
  const [info, setInfo] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [dateField, setDateField] = useState<CampaignDateField>("createdAt");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const load = async () => {
    if (!uuid) return;
    setErr("");
    setLoading(true);
    try {
      const data = await listCampaigns(uuid);
      setItems(data || []);
    } catch (e: any) {
      setErr(
        e?.response?.data?.detail || e?.message || "Failed to load campaigns",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uuid]);

  const statuses = useMemo(
    () => Array.from(new Set(items.map((item) => String(item.status).toUpperCase()))).sort(),
    [items],
  );
  const filteredItems = useMemo(
    () => filterCampaignList(items, { search, status, dateField, dateFrom, dateTo }),
    [items, search, status, dateField, dateFrom, dateTo],
  );
  const filteredCrmTotals = useMemo(
    () => filteredItems.reduce(
      (totals, campaign) => ({
        interested: totals.interested + Number(campaign.crmMetrics?.interested || 0),
        won: totals.won + Number(campaign.crmMetrics?.won || 0),
      }),
      { interested: 0, won: 0 },
    ),
    [filteredItems],
  );
  const hasFilters = Boolean(search || status || dateFrom || dateTo);

  const clearFilters = () => {
    setSearch("");
    setStatus("");
    setDateField("createdAt");
    setDateFrom("");
    setDateTo("");
  };

  const retryQueued = async (campaign: CampaignDoc) => {
    const queued = Number(campaign.metrics?.QUEUED || 0);
    if (!queued || !window.confirm(`Retry ${queued} queued recipient${queued === 1 ? "" : "s"} for ${campaign.name}? Already sent recipients will not be retried.`)) {
      return;
    }
    setRetryingId(campaign._id);
    setErr("");
    setInfo("");
    try {
      const result = await retryQueuedCampaignMessages(uuid, campaign._id);
      setInfo(`Resumed ${result.retried} queued recipient${result.retried === 1 ? "" : "s"}.`);
      await load();
    } catch (e: any) {
      setErr(e?.response?.data?.detail || e?.message || "Failed to retry queued recipients");
    } finally {
      setRetryingId("");
    }
  };

  if (!uuid) return <Alert severity="warning">Loading settings...</Alert>;

  return (
    <Box sx={{ p: 0 }}>
      <Paper sx={{ p: { xs: 2, md: 3 }, border: "1px solid", borderColor: "divider", minWidth: 0 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ gap: 1, flexWrap: "wrap" }}
        >
          <Typography component="h2" variant="h6" sx={{ fontWeight: 700 }}>
            All campaigns
          </Typography>

          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              variant="outlined"
              size="small"
              startIcon={
                loading ? <CircularProgress size={16} /> : <RefreshIcon />
              }
              onClick={load}
              disabled={loading}
            >
              Refresh
            </Button>
          </Stack>
        </Stack>

        {err && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {err}
          </Alert>
        )}
        {info && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {info}
          </Alert>
        )}

        <Box sx={{ mt: 2, py: 2, borderTop: "1px solid", borderBottom: "1px solid", borderColor: "divider" }}>
          <Stack spacing={1.5}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={1.25}
              alignItems={{ md: "center" }}
            >
              <TextField
                size="small"
                label="Search campaigns"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                sx={{ minWidth: { md: 240 }, flex: 1 }}
              />
              <TextField
                select
                size="small"
                label="Status"
                value={status}
                onChange={(event) => setStatus(event.target.value)}
                sx={{ minWidth: 160 }}
              >
                <MenuItem value="">All statuses</MenuItem>
                {statuses.map((campaignStatus) => (
                  <MenuItem key={campaignStatus} value={campaignStatus}>
                    {campaignStatus}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label="Date type"
                value={dateField}
                onChange={(event) => setDateField(event.target.value as CampaignDateField)}
                sx={{ minWidth: 155 }}
              >
                <MenuItem value="createdAt">Created date</MenuItem>
                <MenuItem value="scheduledAt">Scheduled date</MenuItem>
              </TextField>
              <TextField
                size="small"
                type="date"
                label="From"
                value={dateFrom}
                onChange={(event) => setDateFrom(event.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                size="small"
                type="date"
                label="To"
                value={dateTo}
                onChange={(event) => setDateTo(event.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <Button size="small" onClick={clearFilters} disabled={!hasFilters}>
                Clear
              </Button>
            </Stack>

            <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1 }}>
              <Chip label={`${filteredItems.length} campaigns`} variant="outlined" />
              <Chip label={`${filteredCrmTotals.interested} interested leads`} color="primary" variant="outlined" />
              <Chip label={`${filteredCrmTotals.won} customers won`} color="success" variant="outlined" />
            </Stack>
          </Stack>
        </Box>

        {loading ? (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2 }}>
            <CircularProgress size={20} />
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              Loading campaigns...
            </Typography>
          </Stack>
        ) : (
          <TableContainer sx={{ mt: 2 }}>
          <Table size="small" sx={{ minWidth: 1050 }}>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Interested</TableCell>
                <TableCell align="right">Won</TableCell>
                <TableCell>Scheduled</TableCell>
                <TableCell>Next follow-up</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {filteredItems.map((c) => (
                <TableRow key={c._id}>
                  <TableCell>
                    <Typography variant="body2">{c.name}</Typography>
                    {(c.deliveryBatches?.length || 0) > 0 && (
                      <Typography variant="caption" sx={{ opacity: 0.65 }}>
                        {c.deliveryBatches?.length} staggered batches
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={c.status}
                      color={
                        c.status === "COMPLETED"
                          ? "success"
                          : c.status === "SCHEDULED"
                            ? "primary"
                            : c.status === "FAILED"
                              ? "error"
                              : "default"
                      }
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="right">{Number(c.crmMetrics?.interested || 0).toLocaleString()}</TableCell>
                  <TableCell align="right">{Number(c.crmMetrics?.won || 0).toLocaleString()}</TableCell>
                  <TableCell>
                    {c.scheduledAt
                      ? new Date(c.scheduledAt).toLocaleString()
                      : "Send now"}
                  </TableCell>
                  <TableCell>
                    {c.nextFollowUpAt
                      ? new Date(c.nextFollowUpAt).toLocaleString()
                      : c.followUps?.enabled
                        ? "Sequence complete"
                        : "Disabled"}
                  </TableCell>
                  <TableCell>
                    {c.createdAt ? new Date(c.createdAt).toLocaleString() : "-"}
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      {Number(c.metrics?.QUEUED || 0) > 0 && ["READY", "RUNNING", "FAILED", "COMPLETED"].includes(String(c.status).toUpperCase()) && (
                        <Button
                          color="warning"
                          variant="outlined"
                          size="small"
                          startIcon={retryingId === c._id ? <CircularProgress size={14} /> : <ReplayIcon />}
                          disabled={Boolean(retryingId)}
                          onClick={() => retryQueued(c)}
                        >
                          Retry queued ({Number(c.metrics?.QUEUED || 0)})
                        </Button>
                      )}
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => onSelectCampaign(c._id)}
                      >
                        View
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}

              {filteredItems.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} sx={{ opacity: 0.7, py: 4, textAlign: "center" }}>
                    {items.length === 0 ? "No campaigns yet" : "No campaigns match these filters"}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
}
